import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Check, X, Loader2 } from 'lucide-react';

// Edición inline de texto
export function InlineText({ value, onSave, className = '', disabled = false, type = 'text', placeholder = '-', prefix = '' }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    if (editValue === (value || '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(type === 'number' ? (parseFloat(editValue) || 0) : editValue);
      setEditing(false);
    } catch {
      setEditValue(value || '');
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') { setEditValue(value || ''); setEditing(false); }
  };

  if (disabled) {
    const displayVal = type === 'number' && value ? `${prefix}${Number(value).toLocaleString()}` : `${prefix}${value || placeholder}`;
    return <span className={className}>{displayVal}</span>;
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          type={type}
          className="h-7 text-xs min-w-[60px]"
          disabled={saving}
        />
        {saving && <Loader2 className="h-3 w-3 animate-spin" />}
      </div>
    );
  }

  const displayVal = type === 'number' && value ? `${prefix}${Number(value).toLocaleString()}` : `${prefix}${value || placeholder}`;
  return (
    <span
      className={`cursor-pointer hover:bg-muted/60 px-1 py-0.5 rounded transition-colors ${className}`}
      onClick={() => { setEditValue(value || ''); setEditing(true); }}
      title="Click para editar"
    >
      {displayVal}
    </span>
  );
}

// Edición inline de hora (start_time - end_time)
export function InlineTime({ startTime, endTime, onSave, disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(startTime || '');
  const [end, setEnd] = useState(endTime || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (start === (startTime || '') && end === (endTime || '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(start, end);
      setEditing(false);
    } catch {
      setStart(startTime || '');
      setEnd(endTime || '');
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (disabled) {
    return <span className="text-xs">{startTime || '-'} - {endTime || '-'}</span>;
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="h-7 text-xs w-[85px] px-1" />
        <span className="text-xs">-</span>
        <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-7 text-xs w-[85px] px-1" onBlur={handleSave} onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setStart(startTime || ''); setEnd(endTime || ''); setEditing(false); }}} />
        {saving && <Loader2 className="h-3 w-3 animate-spin" />}
      </div>
    );
  }

  return (
    <span
      className="text-xs cursor-pointer hover:bg-muted/60 px-1 py-0.5 rounded transition-colors"
      onClick={() => { setStart(startTime || ''); setEnd(endTime || ''); setEditing(true); }}
      title="Click para editar horario"
    >
      {startTime || '-'} - {endTime || '-'}
    </span>
  );
}

// Edición inline con dropdown
export function InlineSelect({ value, options, onSave, disabled = false, renderValue, className = '' }) {
  const [saving, setSaving] = useState(false);

  const handleChange = async (newValue) => {
    if (newValue === value) return;
    setSaving(true);
    try {
      await onSave(newValue);
    } catch { /* error handled upstream */ }
    finally { setSaving(false); }
  };

  if (disabled) {
    return renderValue ? renderValue(value) : <span className={className}>{value || '-'}</span>;
  }

  return (
    <div className="flex items-center gap-1">
      <Select value={value || ''} onValueChange={handleChange} disabled={saving}>
        <SelectTrigger className={`h-7 text-xs border-dashed ${className}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {saving && <Loader2 className="h-3 w-3 animate-spin" />}
    </div>
  );
}

// Edición inline de fecha
export function InlineDate({ value, onSave, disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleSave = async () => {
    if (editValue === (value || '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(editValue);
      setEditing(false);
    } catch {
      setEditValue(value || '');
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (disabled) {
    return <span className="text-sm">{value ? new Date(value + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}</span>;
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef}
          type="date"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setEditValue(value || ''); setEditing(false); }}}
          className="h-7 text-xs w-[120px]"
        />
        {saving && <Loader2 className="h-3 w-3 animate-spin" />}
      </div>
    );
  }

  return (
    <span
      className="text-sm cursor-pointer hover:bg-muted/60 px-1 py-0.5 rounded transition-colors"
      onClick={() => { setEditValue(value || ''); setEditing(true); }}
      title="Click para editar fecha"
    >
      {value ? new Date(value + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}
    </span>
  );
}
